// Runs `load` until one attempt succeeds, then calls `onLoaded` with its result
// exactly once. Returns a function that stops any further attempts.
//
// Each attempt is passed an AbortSignal and gets `attemptTimeoutsMs[n]` to
// settle, where n is the attempt's index; the last budget repeats. One that has
// not settled by then is aborted rather than awaited: a request can stall in the
// browser (waiting on the auth token, or on a dead pooled connection) and never
// settle at all, with no response and no error. Awaiting such a request is what
// left the capture page on its spinner for good (tray 56790, beta66,
// 2026-09-25). Stopping aborts the attempt in flight as well. `load` should hand
// the signal to its request so an aborted attempt is cancelled, not left running;
// whatever an aborted attempt still produces is ignored here.
//
// Failed and aborted attempts are retried after `retryDelaysMs[n]`; the last
// delay repeats.
export const loadWithRetry = (
  load,
  { onLoaded, onAttemptFailed = () => {}, attemptTimeoutsMs, retryDelaysMs }
) => {
  let done = false;
  let attempt = 0;
  let controller = null;
  let deadlineTimer = null;
  let retryTimer = null;

  const nth = (list, n) => list[Math.min(n, list.length - 1)];

  const run = () => {
    const n = attempt++;
    const timeoutMs = nth(attemptTimeoutsMs, n);
    const attemptController = (controller = new AbortController());
    let settled = false;

    const fail = (err) => {
      if (settled) return;
      settled = true;
      clearTimeout(deadlineTimer);
      if (done) return;
      onAttemptFailed(err, n);
      retryTimer = setTimeout(run, nth(retryDelaysMs, n));
    };

    deadlineTimer = setTimeout(() => {
      attemptController.abort();
      fail(new Error(`attempt ${n + 1} did not settle in ${timeoutMs}ms`));
    }, timeoutMs);

    Promise.resolve()
      .then(() => load(attemptController.signal))
      .then((result) => {
        if (settled) return;
        settled = true;
        clearTimeout(deadlineTimer);
        if (done) return;
        done = true;
        onLoaded(result);
      }, fail);
  };

  run();

  return () => {
    done = true;
    clearTimeout(deadlineTimer);
    clearTimeout(retryTimer);
    controller.abort();
  };
};
